<script setup lang="ts">
import { computed, nextTick, onMounted, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import api from '@/api';
import { trackingSurveyEvent } from '@/api/tracking';
import { DgrDialog, DgrIcon } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Header from '@/components/layouts/header.vue';
import SurveyInput from '@/components/survey/input/survey-input.vue';
import SurveyPromptTemplateCard from '@/components/survey/prompt-template/survey-prompt-template-card.vue';
import {
  DocType,
  FileNameSearchResponse,
  PromptTemplate,
  SearchScope,
  SpecifiedMethod,
  SurveyInputValue,
  SurveyPromptTemplateTrackingInfo,
} from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import {
  parseInsightActionId,
  useInsightActionPrefill,
} from '@/utils/composables/useInsightActionPrefill';
import { debounce } from '@/utils/debounce';
import { specifiedMethodKey } from '@/utils/injectionKeys';
import { convertKeysToCamelCase } from '@/utils/survey/common';
import {
  SEARCH_TO_SURVEY_FILE_SPECIFICATION_QUERY,
  SEARCH_TO_SURVEY_GROUP_MARKS_QUERY,
  SEARCH_TO_SURVEY_USER_MARKS_QUERY,
  SURVEY_TOP_PAGE_NAME,
} from '@/utils/survey/constants';
import { trackSurveyPageView } from '@/utils/survey/tracking';
import {
  useSurveyPromptTemplateCategories,
  useSurveyPromptTemplateFavorites,
  useSurveyPromptTemplates,
} from '@/utils/swr';
import { useTeamInfo } from '@/utils/swr';
import { userSession } from '@/utils/userSession';
import { useEmitter } from '@/utils/vue';

/**
 * Local States
 */

const emitter = useEmitter();
const router = useRouter();
const route = useRoute();
const store = useStore();
const { createSnackbar } = useSnackbar();
const { getCurrentPathForTracking } = useCurrentPathForTracking();

// 入力欄
const initialSearchScope = ref<SearchScope>();
const initialDocTypes = ref<DocType[]>();
const initialSpecifiedFiles = ref<FileNameSearchResponse[]>();
const initialQuestion = ref<string>();
const specifiedMethod = ref<SpecifiedMethod>();
const initialDeepMode = ref<boolean>(false);
const initialGroupId = ref<number | undefined>(undefined);

const pageLimit = 50;
const pageRef = ref(1);
const promptTemplates = ref<Array<PromptTemplate>>([]);
const favorites = ref<Array<PromptTemplate>>([]);
const { data: promptTemplatesData } = useSurveyPromptTemplates(
  pageRef,
  pageLimit,
);
const query = ref<string>('');
const SEARCH_DEBOUNCE_WAIT_MS = 500;
const categoryRefs = ref<HTMLElement[] | undefined>();
const isRequestDialogOpen = ref(false);
const requestContent = ref<string>('');

const surveyPromptTemplateTrackingInfo = ref<
  SurveyPromptTemplateTrackingInfo | undefined
>(undefined);

const { data: favoritesData } = useSurveyPromptTemplateFavorites();
// 契約情報
const { data: teamInfo } = useTeamInfo();
const enableTechnicalLiterature = computed(
  () => teamInfo.value?.enable_technical_literature ?? false,
);

const { data: categories } = useSurveyPromptTemplateCategories();

const categoryIsExpandedMap = ref<Record<string | number, boolean>>({});

watch(promptTemplatesData, newData => {
  const templates = newData?.templates || [];
  const converted = convertKeysToCamelCase(templates) || [];
  if (pageRef.value === 1) {
    promptTemplates.value = converted;
  } else {
    promptTemplates.value.push(...converted);
  }
  if (newData?.has_next_page) {
    pageRef.value += 1;
  }
});
watch(favoritesData, newFavorites => {
  favorites.value = convertKeysToCamelCase(newFavorites) || [];
});

provide(specifiedMethodKey, specifiedMethod);

useInsightActionPrefill('survey', ({ prompt_text: promptText }) => {
  initialQuestion.value = promptText;
});

const visiblePromptTemplates = computed(() => {
  if (!query.value) return promptTemplates.value;

  const lowerQuery = query.value.toLowerCase();
  return promptTemplates.value.filter(template =>
    template.title.toLowerCase().includes(lowerQuery),
  );
});

const visiblePromptTemplateFavorites = computed(() => {
  if (!query.value) return favorites.value;

  const lowerQuery = query.value.toLowerCase();
  return favorites.value.filter(template =>
    template.title.toLowerCase().includes(lowerQuery),
  );
});

const categoryTemplates = computed(() => {
  const visibleCategories =
    categories.value?.filter(
      category =>
        promptTemplates.value.filter(
          template => template.categoryId === category.id,
        ).length > 0,
    ) || [];
  const hasOtherCategory = promptTemplates.value.some(
    template => !template.categoryId,
  );

  const categoriesWithTemplate = (visibleCategories ?? []).map(category => {
    const templates = visiblePromptTemplates.value.filter(
      template => template.categoryId === category.id,
    );
    return {
      ...category,
      hasNewTemplates: templates.some(getIsNew),
      templates,
    };
  });
  const otherTemplates = visiblePromptTemplates.value.filter(
    template => !template.categoryId,
  );

  return [
    {
      id: 'favorites',
      name: 'お気に入り',
      hasNewTemplates: false,
      templates: visiblePromptTemplateFavorites.value,
    },
    ...categoriesWithTemplate,
    ...(hasOtherCategory
      ? [
          {
            id: 'others',
            name: 'その他',
            hasNewTemplates: otherTemplates.some(getIsNew),
            templates: otherTemplates,
          },
        ]
      : []),
  ];
});

/**
 * Lifecycle Hooks
 */

onMounted(async () => {
  try {
    await trackSurveyPageView(
      SURVEY_TOP_PAGE_NAME,
      getCurrentPathForTracking(),
    );
    if (route.query.mode === 'user_marks') {
      initialSearchScope.value = 'user_marks';
      initialDocTypes.value = enableTechnicalLiterature.value
        ? ['article', 'report', 'research_paper', 'patent']
        : ['article', 'report'];
      initialQuestion.value = SEARCH_TO_SURVEY_USER_MARKS_QUERY;
    }
    if (route.query.mode === 'group_marks') {
      initialSearchScope.value = 'group_marks';
      initialGroupId.value = route.query.group_id
        ? Number(route.query.group_id)
        : undefined;
      initialDocTypes.value = enableTechnicalLiterature.value
        ? ['article', 'report', 'research_paper', 'patent']
        : ['article', 'report'];
      initialQuestion.value = SEARCH_TO_SURVEY_GROUP_MARKS_QUERY;
    }

    if (route.query.user_document_id) {
      const userDocument = await api.getUserDocumentById(
        route.query.user_document_id as string,
      );
      const specifiedFile = {
        id: userDocument.id,
        filename: userDocument.filename,
        file_type: userDocument.file_type,
        url: userDocument.url,
        modified_at: dayjs(userDocument.modified_at).format('YYYY-MM-DD'),
        last_updated_by: userDocument.last_updated_by,
      };
      initialSpecifiedFiles.value = [specifiedFile];
      initialSearchScope.value = 'specified_files';
      specifiedMethod.value = 'user_document_search';
      initialQuestion.value = SEARCH_TO_SURVEY_FILE_SPECIFICATION_QUERY;

      // 計測
      await trackingSurveyEvent('document_specify', {
        page: {
          name: SURVEY_TOP_PAGE_NAME,
          url: getCurrentPathForTracking(),
        },
        specified_method: 'user_document_search',
        document: {
          type: 'user_document',
          id: specifiedFile.id,
        },
        session_id: null,
      });
    }
  } catch (error) {
    throw new Error(`survey-top.vue onMounted error: ${error}`);
  }
});

/**
 * Handlers
 */

// 入力値のクエリパラメータをqueryにしていないのは、ヘッダーの検索窓の値でqueryを使用しており、そちらにも値が入ってしまうため
const startSurvey = async ({
  question,
  filterInfo,
  deepMode,
}: SurveyInputValue) => {
  const surveySession = await api.createSurveySession(question);
  const surveySessionId = surveySession.session_id;

  await store.dispatch('survey/setSessionAllState', {
    sessionId: surveySessionId,
    question,
    filterInfo,
    deepMode,
    specifiedMethod,
    executionFrom: route.query.from ?? 'survey_page',
    recommendedActionId: parseInsightActionId(route.query),
    surveyType: 'survey',
    surveyPromptTemplate: surveyPromptTemplateTrackingInfo.value,
  });

  // サイドメニューの履歴に反映させるため通知
  emitter.emit('update-survey-session', {
    session_id: surveySessionId,
    title: question,
    updated_at: new Date().toISOString(),
  });

  await router.push({
    name: 'surveySession',
    params: { survey_session_id: surveySessionId },
  });
};

const getSurveyPromptTemplateTrackingInfo = (
  template: PromptTemplate,
): SurveyPromptTemplateTrackingInfo => {
  const category = categories.value?.find(c => c.id === template.categoryId);

  return {
    id: template.id,
    name: template.title,
    category: category
      ? {
          id: category.id,
          name: category.name,
        }
      : undefined,
  };
};

const applyPromptTemplate = async (
  prompt: string,
  template: PromptTemplate,
  isFavorite: boolean,
) => {
  // NOTE: プロンプトテンプレートを適用した後に、一度入力欄を消して再度同じプロンプトテンプレートを適用した場合を考慮して一度リセット
  initialQuestion.value = '';
  initialDeepMode.value = false;
  await nextTick();

  initialQuestion.value = prompt;
  initialDeepMode.value = template.deepMode;

  const surveyPromptTemplate: SurveyPromptTemplateTrackingInfo =
    getSurveyPromptTemplateTrackingInfo(template);
  surveyPromptTemplate.is_favorited = isFavorite;
  surveyPromptTemplateTrackingInfo.value = surveyPromptTemplate;

  // 計測
  await trackingSurveyEvent('add_prompt', {
    page: {
      name: 'survey_top',
      url: getCurrentPathForTracking(),
    },
    survey_prompt_template: surveyPromptTemplate,
  });
};

const getIsFavorite = (template: PromptTemplate) => {
  return favorites.value.find(t => t.id === template.id) !== undefined;
};

const getIsNew = (template: PromptTemplate) => {
  return dayjs().diff(dayjs(template.createdAt), 'day') <= 14;
};

const toggleFavorite = async (template: PromptTemplate) => {
  const isFavorite = getIsFavorite(template);
  if (isFavorite) {
    try {
      await api.deleteSurveyPromptTemplateFavorite(template.id);
      favorites.value = favorites.value.filter(t => t.id !== template.id);
      createSnackbar({
        message: 'お気に入りを解除しました',
        type: 'success',
      });
      await trackingSurveyEvent('prompt_template_favorites_remove', {
        page: {
          name: 'survey_top',
          url: getCurrentPathForTracking(),
        },
        survey_prompt_template: getSurveyPromptTemplateTrackingInfo(template),
      });
    } catch (error) {
      createSnackbar({
        message: 'お気に入りの解除に失敗しました',
        type: 'error',
      });
      throw error;
    }
  } else {
    try {
      await api.createSurveyPromptTemplateFavorite(template.id);
      favorites.value.unshift(template);
      createSnackbar({
        message: 'お気に入りに登録しました',
        type: 'success',
      });
      await trackingSurveyEvent('prompt_template_favorites_add', {
        page: {
          name: 'survey_top',
          url: getCurrentPathForTracking(),
        },
        survey_prompt_template: getSurveyPromptTemplateTrackingInfo(template),
      });
    } catch (error) {
      createSnackbar({
        message: 'お気に入りの登録に失敗しました',
        type: 'error',
      });
      throw error;
    }
  }
};

const scrollToCategory = (categoryId: number | string) => {
  const categoryEl = categoryRefs.value?.find(
    r => r.dataset.categoryId === String(categoryId),
  );
  if (!categoryEl) return;

  nextTick(() => {
    categoryEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
};

const sendRequest = async () => {
  if (!requestContent.value) return;

  try {
    await api.createSurveyPromptTemplateRequest(requestContent.value);

    createSnackbar({
      message: 'リクエストを送信しました',
      type: 'success',
    });
    isRequestDialogOpen.value = false;
    requestContent.value = '';
    await trackingSurveyEvent('prompt_template_request', {
      page: {
        name: 'survey_top',
        url: getCurrentPathForTracking(),
      },
    });
  } catch (error) {
    createSnackbar({
      message: 'リクエストを送信できませんでした',
      type: 'error',
    });
    throw error;
  }
};

watch(
  query,
  debounce(async () => {
    if (query.value.length > 0) {
      // プロンプトの検索ワードは機密情報が入る可能性があるため計測しない
      await trackingSurveyEvent('prompt_template_search', {
        page: {
          name: 'survey_top',
          url: getCurrentPathForTracking(),
        },
      });
    }
  }, SEARCH_DEBOUNCE_WAIT_MS),
);

watch(categoryTemplates, newCategoryTemplates => {
  if (!newCategoryTemplates) return;

  newCategoryTemplates.forEach(category => {
    categoryIsExpandedMap.value[category.id] =
      userSession.getSurveyTemplatePromptCategoryIsExpanded(category.id);
  });
});

const toggleCategoryExpanded = (categoryId: string | number) => {
  categoryIsExpandedMap.value[categoryId] =
    !categoryIsExpandedMap.value[categoryId];
  userSession.setSurveyTemplatePromptCategoryIsExpanded(
    categoryId,
    categoryIsExpandedMap.value[categoryId],
  );
};
</script>

<template>
  <div class="content-wrapper">
    <Header title="要約生成" :is-one-line="true" header-width="912px" />
    <div class="content">
      <SurveyInput
        class="survey-input"
        :enable-transform-input="false"
        :initial-search-scope="initialSearchScope"
        :initial-doc-types="initialDocTypes"
        :initial-specified-files="initialSpecifiedFiles"
        :initial-question="initialQuestion"
        :initial-deep-mode="initialDeepMode"
        :initial-group-id="initialGroupId"
        :page-name="SURVEY_TOP_PAGE_NAME"
        @submit="startSurvey"
      />
      <div
        v-if="promptTemplates && promptTemplates.length"
        class="survey-prompt-templates"
      >
        <div class="prompt-templates-header c-title-bold--xm">
          プロンプトテンプレート
          <button
            class="c-btn c-btn--small c-btn--auto c-outlineBtn"
            @click="isRequestDialogOpen = true"
          >
            <DgrIcon class="request-icon" name="paperplane" size="small" />
            テンプレートのリクエスト
          </button>
        </div>
        <div class="search">
          <DgrIcon
            class="search-icon"
            size="small"
            name="search"
            :keep-fill="false"
          />
          <input
            class="c-textInput"
            type="text"
            placeholder="プロンプトテンプレートを検索"
            v-model="query"
          />
        </div>
        <div class="categories" v-if="categories && categories.length">
          <button
            v-for="category in categoryTemplates.filter(
              c => c.id !== 'favorites',
            )"
            :key="category.id"
            class="c-btn c-btn--small"
            @click="scrollToCategory(category.id)"
          >
            {{ category.name }}
          </button>
        </div>
        <div
          class="category-templates"
          v-for="category in categoryTemplates"
          :key="category.id"
        >
          <span
            ref="categoryRefs"
            :data-category-id="category.id"
            class="category-title"
            @click="toggleCategoryExpanded(category.id)"
          >
            <DgrIcon
              :name="
                categoryIsExpandedMap[category.id]
                  ? 'disclosure-triangle-open'
                  : 'disclosure-triangle-close'
              "
              class="category-toggle-icon"
            />
            <span class="c-title-bold--m">{{ category.name }}</span>
            <span class="c-text-regular--m template-count"
              >{{ category.templates.length }}件</span
            >
            <span class="new c-text-regular--s" v-if="category.hasNewTemplates"
              >新着</span
            >
          </span>
          <template v-if="categoryIsExpandedMap[category.id]">
            <div
              class="prompt-template-cards-wrapper"
              v-if="category.templates.length"
            >
              <SurveyPromptTemplateCard
                v-for="(template, idx) in category.templates"
                :key="`prompt-template-${category.id}-${idx}`"
                :template="template"
                :is-favorite="getIsFavorite(template)"
                :is-new="category.id !== 'favorites' && getIsNew(template)"
                @apply-prompt-template="applyPromptTemplate"
                @toggle-favorite="toggleFavorite"
              />
            </div>
            <span
              v-else-if="category.id === 'favorites' && query.length === 0"
              class="c-text-regular--s"
            >
              まだお気に入りに設定したテンプレートはありません
            </span>
          </template>
        </div>
      </div>
      <DgrDialog
        v-model:open="isRequestDialogOpen"
        title="テンプレートのリクエスト"
        submit-label="送信"
        @submit="sendRequest"
        @cancel="isRequestDialogOpen = false"
      >
        <template #description>
          <div class="modal-body">
            <div class="c-formBlock">
              <div class="c-formBlock__label">リクエスト</div>
              <div class="subtitle c-text-regular--s">
                ご要望がすべて反映されるわけではありません。あらかじめご了承ください。
              </div>
              <textarea
                class="request-content c-textArea"
                placeholder="追加して欲しいテンプレートの概要について記載してください"
                v-model.trim="requestContent"
              ></textarea>
            </div>
          </div>
        </template>
        <template #footer>
          <button
            class="c-btn c-btn--auto c-outlineBtn"
            @click="isRequestDialogOpen = false"
          >
            キャンセル
          </button>
          <button
            class="c-btn c-btn--auto c-btn--AnewsPrimary"
            :class="{
              disabled: !requestContent,
            }"
            :disabled="!requestContent"
            @click="sendRequest"
          >
            送信
          </button>
        </template>
      </DgrDialog>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.content-wrapper {
  width: 100%;
}

.content {
  padding: 16px;
  max-width: 912px;
  margin: 0 auto;

  .container {
    margin: 0;
  }
}

.survey-input {
  position: sticky;
  // ヘッダー + 要約のヘッダー (63px) + 16px padding
  top: calc(var(--layout-sticky-top, 64px) + 63px + 16px);
  z-index: 1;
  box-shadow: 0 1px 5px 0 rgba(74, 74, 74, 0.2);
}

.search {
  display: flex;
  align-items: center;

  .c-textInput {
    padding-left: 36px;
    outline: none;
    margin-left: -36px;
  }
  .search-icon {
    position: relative;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    height: 32px;
    border: 0;
    background: #fff;
    color: $color-gray1000;
  }
}

.categories {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  align-items: flex-start;

  padding: 16px;
  background-color: $color-gray200;
  border-radius: 4px;

  .c-btn {
    border: 0;
    width: fit-content;
  }
}

.category-templates {
  .category-title {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 4px 0;
    border-radius: 4px;
    // ヘッダーの高さ + 入力欄の高さ + padding（実際の高さはJS側で上書きする）
    scroll-margin-top: calc(
      var(--layout-sticky-top-under-tabs, 112px) + 118px + 32px
    );

    &:hover {
      background-color: $color-gray200;
    }

    .category-toggle-icon {
      margin-right: -8px;
    }

    .template-count {
      color: $color-gray1000;
    }
    .new {
      padding: 0 4px;
      background-color: $color-green100;
      color: $color-green600;
      border-radius: 2px;
    }
  }
}

.survey-prompt-templates {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 4px;
}

.prompt-templates-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  .request-icon {
    flex-shrink: 0;
    margin-right: 4px;
  }
}

.prompt-template-cards-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
}

.more-button {
  display: block;
  margin: 16px auto 0 auto;
  background-color: $color-white;
}

.modal-body {
  width: 616px;

  .subtitle {
    margin: 4px 0;
    color: $color-gray800;
  }

  .request-context {
    outline: none;
  }
}
</style>
